import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, FolderOpen, Loader2, Search } from 'lucide-react';
import { Customer, Quote } from '../../types';
import { Button } from '../Button';
import { OrderLinesPicker } from './OrderLinesPicker';
import {
  computeTrancheNet,
  formatEur,
  formatQty,
  initialOrderSelection,
  linesFromSelection,
  openOrderLines,
  OrderSelection,
  parseOrderQty,
  validateOrderLines,
} from '../../services/quoteOrderTranches';
import { auftragswertCentsAusEingabe, centsAlsEingabe } from '../../services/erpSteering';
import { pendingFlatInclusionDecisions, type FlatInclusionDecision } from '../../services/flatRateLogic';
import { planSupplementForProjectLv, proposeSupplementMapping, type SupplementMapping, type SupplementPlan } from '../../services/quoteSupplementLogic';
import {
  loadSupplementContext,
  searchSupplementProjects,
  type SupplementContext,
  type SupplementProjectOption,
} from '../../services/quoteSupplementService';
import {
  buildSupplementFormValues,
  forcedAppendText,
  isNepStep,
  newServiceIdOfStep,
  setMappingTarget,
  suggestSupplementOrderValueCents,
  supplementConfirmBlockers,
  supplementCustomerHint,
  supplementMappingRows,
  supplementPlanProblems,
  supplementProjectLabel,
  supplementStepText,
  supplementTargetOptions,
  type SupplementFormValues,
} from '../../services/supplementAcceptForm';
import { describeError } from '../../services/errorMessage';

/**
 * Annahme eines Angebots als NACHTRAG zu einem bestehenden Projekt: es
 * entsteht KEIN neues Projekt. Die Positionen erhöhen die Vertragsmenge
 * vorhandener Projektpositionen oder werden mit LV-Nr. „N<seq>.<lfd>"
 * angehängt. Rumpf und Fußzeile des Annahmedialogs im Modus „Nachtrag" —
 * Kopf und Moduswahl stellt AcceptQuoteDialog.
 *
 * Geschrieben wird hier nichts: onConfirm übergibt die Werte dem Editor
 * (acceptQuoteAsSupplement). Formularlogik in services/supplementAcceptForm.ts.
 */
interface Props {
  quote: Quote;
  customer: Customer;
  busy?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (values: SupplementFormValues) => void;
}

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const SupplementAcceptForm: React.FC<Props> = ({ quote, customer, busy, error, onCancel, onConfirm }) => {
  // ─── Projektauswahl ─────────────────────────────────────────────────────────
  // Vorbelegt aus dem Bezug des Nachtragsangebots; sonst sucht der Bearbeiter.
  const presetRef = quote.details?.nachtragZu;
  const [projectId, setProjectId] = useState(() => presetRef?.projectId || '');
  // Anzeige bis der Kontext geladen ist (Name aus der Auswahl bzw. dem Bezug).
  const [pickedLabel, setPickedLabel] = useState(() => (presetRef ? supplementProjectLabel({
    projectNumber: presetRef.projectNumber || '', projectName: presetRef.projectName || '',
  }) : ''));
  const [searchOpen, setSearchOpen] = useState(() => !presetRef?.projectId);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SupplementProjectOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const searchRequestRef = useRef(0);

  // Suche entprellt; nur die Antwort der LETZTEN Anfrage zählt. Ohne Suchtext
  // liefert der Dienst die Projekte des Kunden.
  useEffect(() => {
    if (!searchOpen) return;
    const request = ++searchRequestRef.current;
    setOptionsLoading(true);
    setOptionsError('');
    const timer = setTimeout(() => {
      searchSupplementProjects(query.trim(), customer.id || undefined)
        .then((list) => { if (request === searchRequestRef.current) setOptions(list); })
        .catch((e) => { if (request === searchRequestRef.current) { setOptions([]); setOptionsError(describeError(e)); } })
        .finally(() => { if (request === searchRequestRef.current) setOptionsLoading(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, customer.id, searchOpen]);

  // ─── Kontext des Zielprojekts ───────────────────────────────────────────────
  const [ctx, setCtx] = useState<SupplementContext | null>(null);
  const [ctxError, setCtxError] = useState('');
  const [mapping, setMapping] = useState<SupplementMapping>({});
  const [flatDecisions, setFlatDecisions] = useState<Partial<Record<string, FlatInclusionDecision>>>({});
  const [endDate, setEndDate] = useState('');
  const [orderValueText, setOrderValueText] = useState('');
  // Von Hand geaendert? Dann folgt der Wert nicht mehr Umfang und Projekt.
  const [orderValueTouched, setOrderValueTouched] = useState(false);

  useEffect(() => {
    setCtx(null);
    setCtxError('');
    setFlatDecisions({});
    setOrderValueTouched(false);
    if (!projectId) return;
    let alive = true;
    loadSupplementContext(projectId, quote)
      .then((c) => {
        if (!alive) return;
        setCtx(c);
        setEndDate(c.endDate ? c.endDate.slice(0, 10) : '');
        // Startwert der Zuordnung: übernommene Projektpositionen bzw. eindeutige
        // Treffer (gleiche Einheit + Titel), sonst „Neue Position".
        try {
          setMapping(proposeSupplementMapping(c.services, quote.items || []));
        } catch (e) {
          setMapping({});
          setCtxError(`Zuordnung konnte nicht vorgeschlagen werden: ${describeError(e)}`);
        }
      })
      .catch((e) => { if (alive) setCtxError(describeError(e)); });
    return () => { alive = false; };
    // Einmal je Projekt: Vorschau und Zuordnung rechnen danach lokal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, quote.id]);

  const pickProject = (o: SupplementProjectOption) => {
    if (busy || o.blocker) return;
    setPickedLabel(supplementProjectLabel(o));
    setProjectId(o.id);
    setSearchOpen(false);
  };

  // ─── Umfang (wie im Projektweg) ─────────────────────────────────────────────
  const fullLines = useMemo(() => openOrderLines(quote), [quote]);
  const canPartial = fullLines.length > 0;
  const [scope, setScope] = useState<'full' | 'partial'>('full');
  const [selection, setSelection] = useState<OrderSelection>(() => initialOrderSelection(quote, true));
  const partialLines = useMemo(() => linesFromSelection(selection), [selection]);
  const scopeLines = scope === 'partial' ? partialLines : fullLines;
  const scopeErrors = useMemo(
    () => (scope === 'partial' ? validateOrderLines(quote, partialLines) : []),
    [scope, quote, partialLines],
  );
  const unreadableQty = scope === 'partial'
    && Object.values(selection).some((v) => v.on && !((parseOrderQty(v.text) ?? 0) > 0));
  const net = useMemo(
    () => (scopeLines.length > 0 && scopeErrors.length === 0 ? computeTrancheNet(quote, scopeLines) : 0),
    [quote, scopeLines, scopeErrors.length],
  );

  // ─── Plan (Vorschau) ────────────────────────────────────────────────────────
  const planResult = useMemo((): { plan: SupplementPlan | null; error?: string } => {
    if (!ctx || scopeLines.length === 0 || scopeErrors.length > 0) return { plan: null };
    try {
      return { plan: planSupplementForProjectLv(ctx.services, quote.items || [], scopeLines, mapping, { seq: ctx.seq, includeNep: true }) };
    } catch (e) {
      return { plan: null, error: `Vorschau nicht möglich: ${describeError(e)}` };
    }
  }, [ctx, quote.items, scopeLines, scopeErrors.length, mapping]);
  const plan = planResult.plan;
  const planProblems = [
    ...(planResult.error ? [planResult.error] : []),
    ...(plan ? supplementPlanProblems(plan) : []),
  ];
  // Neue Positionen, für die „in der Monatspauschale enthalten?" zu klären ist
  // (nur in Projekten MIT Pauschale) — wie im Dialog „Rest beauftragen".
  const flatDecisionIds = useMemo(
    () => (plan ? pendingFlatInclusionDecisions(plan.services, plan.newServiceIds, {}) : []),
    [plan],
  );
  const pendingFlat = flatDecisionIds.filter((id) => !flatDecisions[id]);

  const rows = useMemo(
    () => supplementMappingRows(quote.items, scopeLines, mapping, ctx?.services),
    [quote.items, scopeLines, mapping, ctx],
  );
  const targetOptions = useMemo(() => supplementTargetOptions(ctx?.services), [ctx]);

  // ─── Auftragswert ───────────────────────────────────────────────────────────
  const suggestedCents = ctx ? suggestSupplementOrderValueCents(ctx.orderValueCents, net) : null;
  useEffect(() => {
    if (!orderValueTouched && ctx) setOrderValueText(suggestedCents == null ? '' : centsAlsEingabe(suggestedCents));
  }, [suggestedCents, orderValueTouched, ctx]);
  const orderValueCents = auftragswertCentsAusEingabe(orderValueText);

  const [orderDate, setOrderDate] = useState(todayIso);
  const [customerRef, setCustomerRef] = useState('');

  const blockers = supplementConfirmBlockers({
    projectId,
    ctxLoaded: !!ctx,
    ctxError: ctxError || undefined,
    ctxBlocker: ctx?.blocker,
    lines: scopeLines,
    scopeErrors,
    unreadableQty,
    plan,
    planError: planResult.error,
    pendingFlat,
  });
  const canConfirm = !busy && blockers.length === 0;
  const customerHint = supplementCustomerHint(ctx?.customerId, customer.id || quote.customerId);

  const handleConfirm = () => {
    if (!canConfirm || !plan) return;
    onConfirm(buildSupplementFormValues({
      projectId,
      mapping,
      scope,
      partialLines,
      orderDate,
      customerRef,
      flatDecisions,
      newServiceIds: plan.newServiceIds,
      endDate,
      ctxEndDate: ctx?.endDate,
      orderValueCents,
      ctxOrderValueCents: ctx?.orderValueCents ?? null,
    }));
  };

  return (
    <>
      <div className="p-6 space-y-4">
        {/* Zielprojekt */}
        <div>
          <label className={labelClass}>Projekt *</label>
          {projectId && !searchOpen ? (
            <div className="mt-1 flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
              <FolderOpen className="w-5 h-5 shrink-0 text-teal-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-800 truncate">{ctx ? supplementProjectLabel(ctx) : pickedLabel || 'Projekt'}</div>
                {ctx ? (
                  <div className="text-xs text-slate-500">
                    Nachtrag {ctx.seq} — neue Positionen bekommen die LV-Nr. N{ctx.seq}.1, N{ctx.seq}.2 …
                    {ctx.mainQuote ? ` · Hauptauftrag ${ctx.mainQuote.orderNumber || ctx.mainQuote.quoteNumber}` : ''}
                  </div>
                ) : !ctxError ? (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Projekt wird geladen…</div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                disabled={busy}
                className="shrink-0 text-xs font-bold text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anderes Projekt
              </button>
            </div>
          ) : (
            <div className="mt-1 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  autoFocus
                  className={`${inputClass} pl-9`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Projektnummer oder -name suchen…"
                  disabled={busy}
                />
              </div>
              <div className="border border-slate-200 rounded-xl max-h-60 overflow-y-auto divide-y divide-slate-100">
                {optionsLoading ? (
                  <p className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Projekte werden gesucht…</p>
                ) : optionsError ? (
                  <p className="px-4 py-3 text-sm text-red-600">{optionsError}</p>
                ) : options.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500">
                    {query.trim() ? 'Kein Projekt gefunden.' : 'Keine Projekte dieses Kunden — bitte nach Nummer oder Name suchen.'}
                  </p>
                ) : (
                  options.map((o) => {
                    const otherCustomer = !!o.customerId && !!customer.id && o.customerId !== customer.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => pickProject(o)}
                        disabled={busy || !!o.blocker}
                        title={o.blocker || undefined}
                        className={`w-full text-left px-4 py-2.5 transition-colors disabled:cursor-not-allowed ${o.id === projectId ? 'bg-blue-50/60' : 'hover:bg-slate-50'} ${o.blocker ? 'opacity-60' : ''}`}
                      >
                        <div className={`text-sm font-semibold ${o.blocker ? 'text-slate-400' : 'text-slate-800'}`}>
                          {supplementProjectLabel(o)}
                          {otherCustomer && <span className="ml-2 text-[11px] font-semibold text-amber-700">anderer Kunde</span>}
                        </div>
                        {o.blocker && <div className="text-[11px] text-slate-500">{o.blocker}</div>}
                      </button>
                    );
                  })
                )}
              </div>
              {projectId && (
                <button type="button" onClick={() => setSearchOpen(false)} disabled={busy} className="text-xs text-blue-600 hover:underline ml-1">
                  Bisherige Auswahl behalten
                </button>
              )}
            </div>
          )}
        </div>

        {(ctxError || ctx?.blocker) && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{ctxError || ctx?.blocker}</span>
          </div>
        )}
        {customerHint && (
          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{customerHint}</span>
          </div>
        )}

        {/* Umfang der Beauftragung — wie beim Projektweg. */}
        {canPartial && (
          <div className="space-y-3">
            <label className={labelClass}>Umfang der Beauftragung</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {([
                ['full', 'Vollständig beauftragt', 'Alle Positionen mit allen Mengen.'],
                ['partial', 'Teilbeauftragung', 'Nur ein Teil — der Rest bleibt am Angebot offen.'],
              ] as const).map(([key, title, hint]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setScope(key)}
                  disabled={busy}
                  className={`text-left rounded-xl border px-4 py-3 transition-all ${scope === key ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/20' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="text-sm font-bold text-slate-800">{title}</div>
                  <div className="text-xs text-slate-500">{hint}</div>
                </button>
              ))}
            </div>
            {scope === 'partial' && (
              <>
                <OrderLinesPicker quote={quote} selection={selection} onChange={setSelection} disabled={busy} />
                {partialLines.length === 0 ? (
                  <p className="text-xs font-semibold text-amber-700">Bitte mindestens eine Position mit Menge wählen.</p>
                ) : scopeErrors.length > 0 ? (
                  <p className="text-xs font-semibold text-red-600">{scopeErrors.join(' ')}</p>
                ) : null}
              </>
            )}
            <p className="text-sm text-slate-600">
              Nachtrag <strong className="text-slate-800">{formatEur(net)}</strong>
              <span className="text-slate-400"> netto{scope === 'partial' ? `, Angebot ${formatEur(quote.netTotal)}` : ''}</span>
            </p>
          </div>
        )}

        {/* Zuordnung je beauftragter Position. */}
        {ctx && rows.length > 0 && (
          <div>
            <label className={labelClass}>Zuordnung im Projekt-LV</label>
            <div className="mt-1 border border-slate-200 rounded-xl divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.item.id} className="px-4 py-2.5 space-y-1.5">
                  <div className="text-sm text-slate-700">
                    <span className="font-mono text-xs text-slate-400 mr-2">{r.item.posNumber || r.item.lvPosition}</span>
                    {r.item.title}
                    <span className="text-slate-400">
                      {' · '}{formatQty(r.quantity)} {r.item.unit} à {formatEur(Number(r.item.unitPrice) || 0)}
                      {Number(r.item.discountPercent) > 0 ? ` (−${formatQty(Number(r.item.discountPercent))} %)` : ''}
                    </span>
                  </div>
                  <select
                    className={`${inputClass} ${r.targetMissing ? 'border-red-300 bg-red-50/40' : ''}`}
                    value={r.value}
                    onChange={(e) => setMapping((prev) => setMappingTarget(prev, r.item.id, e.target.value))}
                    disabled={busy}
                    aria-label={`Ziel für ${r.item.title}`}
                  >
                    {r.targetMissing && <option value={r.value} disabled>Projektposition fehlt — bitte neu wählen</option>}
                    {targetOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1 ml-1">
              Projektposition wählen = deren Vertragsmenge steigt (erbracht und abgerechnet bleiben). „Neue Position“ hängt sie mit
              N-Nummer an. Weicht Einzelpreis oder Einheit ab, entsteht trotzdem eine neue Position.
            </p>
          </div>
        )}

        {/* Vorschau: was im Projekt-LV passiert. */}
        {plan && plan.steps.length > 0 && (
          <div>
            <label className={labelClass}>Änderung im Projekt-LV</label>
            <div className="mt-1 border border-slate-200 rounded-xl divide-y divide-slate-100 text-sm">
              {plan.steps.map((s, i) => {
                const newId = newServiceIdOfStep(s, plan.newServiceIds);
                const forced = forcedAppendText(s);
                return (
                  <div key={`${s.itemId}:${i}`} className="px-4 py-2">
                    <div className="text-slate-700">
                      <span className="font-mono text-xs text-slate-400 mr-2">{s.lvPosition}</span>
                      {s.kind === 'append'
                        ? <span className="font-semibold text-blue-700">{supplementStepText(s)}</span>
                        : <span>{supplementStepText(s)}</span>}
                    </div>
                    {forced && <div className="text-[11px] font-semibold text-amber-700">{forced}</div>}
                    {!forced && s.kind === 'increase' && s.priceDiffers && (
                      <div className="text-[11px] font-semibold text-amber-700">
                        Preis im Projekt {formatEur(s.priceDiffers.project)}, im Nachtrag {formatEur(s.priceDiffers.quote)}.
                      </div>
                    )}
                    {isNepStep(s, quote.items) && (
                      <div className="text-[11px] text-slate-500">NEP — wird als Eventualposition angehängt, nicht beauftragt.</div>
                    )}
                    {s.bestandteileHinweis && <div className="text-[11px] font-semibold text-amber-700">{s.bestandteileHinweis}</div>}
                    {s.packageBlock && (
                      <div className="mt-1 flex gap-1.5 text-xs font-semibold text-red-700">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>{s.packageBlock}</span>
                      </div>
                    )}
                    {newId && flatDecisionIds.includes(newId) && (
                      <div className="mt-1.5 flex items-center gap-2 text-xs">
                        <span className="font-semibold text-slate-600">In der Monatspauschale enthalten?</span>
                        {(['included', 'excluded'] as const).map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setFlatDecisions((prev) => ({ ...prev, [newId]: d }))}
                            disabled={busy}
                            className={`px-2.5 py-1 rounded-lg border font-bold ${flatDecisions[newId] === d ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}
                          >
                            {d === 'included' ? 'ja' : 'nein'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {pendingFlat.length > 0 && (
              <p className="text-xs font-semibold text-amber-700 mt-1 ml-1">
                Für jede neue Position bitte entscheiden: in der Monatspauschale enthalten — ja oder nein.
              </p>
            )}
          </div>
        )}
        {planProblems.length > 0 && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{planProblems.join(' ')}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Bestellnummer Kunde</label>
            <input className={inputClass} value={customerRef} placeholder="optional" onChange={(e) => setCustomerRef(e.target.value)} disabled={busy} />
          </div>
          <div>
            <label className={labelClass}>Beauftragt am</label>
            <input type="date" className={inputClass} value={orderDate} onChange={(e) => setOrderDate(e.target.value)} disabled={busy} />
          </div>
        </div>

        {ctx && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Auftragswert netto neu gesamt (EUR)</label>
              <input
                className={inputClass}
                inputMode="decimal"
                value={orderValueText}
                placeholder="0,00"
                onChange={(e) => { setOrderValueTouched(true); setOrderValueText(e.target.value); }}
                disabled={busy}
              />
              <p className="text-[11px] text-slate-500 mt-1 ml-1">
                {orderValueText.trim() !== '' && orderValueCents === null
                  ? <span className="text-amber-700 font-semibold">Keine gültige Zahl — der Auftragswert bleibt unverändert.</span>
                  : ctx.orderValueCents != null
                    ? `Bisher ${formatEur(ctx.orderValueCents / 100)} + dieser Nachtrag ${formatEur(net)}. Leer = unverändert.`
                    : 'Bisher kein Auftragswert fixiert — Gesamtwert (Hauptauftrag + Nachtrag) eintragen oder leer lassen.'}
              </p>
            </div>
            <div>
              <label className={labelClass}>Projektende</label>
              <input type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={busy} />
              <p className="text-[11px] text-slate-500 mt-1 ml-1">Nur ändern, wenn sich die Laufzeit verlängert.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Button>
        <Button onClick={handleConfirm} disabled={!canConfirm} title={!busy && blockers.length > 0 ? blockers[0] : undefined}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Nachtrag wird eingetragen…</> : 'Nachtrag eintragen'}
        </Button>
      </div>
    </>
  );
};
